const GA_MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const GA_DEBUG = (process.env.REACT_APP_GA_DEBUG || '').toLowerCase() === 'true';
const GA_SCRIPT_ID = 'ga4-gtag-script';

let analyticsInitialized = false;
let lastPageView = {
  path: '',
  timestamp: 0,
};

const hasBrowserContext = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const analyticsEnabled = () => Boolean(GA_MEASUREMENT_ID) && hasBrowserContext();

const sanitizeEventName = (value) => {
  const cleaned = (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (!cleaned) return 'event';
  if (/^[0-9]/.test(cleaned)) return `e_${cleaned}`;
  return cleaned.slice(0, 40);
};

const sanitizeEventParams = (params = {}) => {
  const result = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'number') {
      if (Number.isFinite(value)) result[key] = value;
      return;
    }
    if (typeof value === 'boolean') {
      result[key] = value ? 1 : 0;
      return;
    }
    const asString = String(value).trim();
    if (asString) {
      result[key] = asString.slice(0, 100);
    }
  });
  return result;
};

const ensureGtagReady = () => {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

export const initAnalytics = () => {
  if (!analyticsEnabled() || analyticsInitialized) {
    return;
  }

  ensureGtagReady();
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    ...(GA_DEBUG ? { debug_mode: true } : {}),
  });

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  }

  analyticsInitialized = true;
};

export const trackPageView = ({ pagePath, pageTitle, ...extraParams } = {}) => {
  if (!analyticsEnabled()) return;
  initAnalytics();

  const path = pagePath || `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const now = Date.now();

  // React StrictMode may double-invoke effects in development.
  if (path === lastPageView.path && now - lastPageView.timestamp < 750) {
    return;
  }
  lastPageView = { path, timestamp: now };

  window.gtag('event', 'page_view', sanitizeEventParams({
    page_path: path,
    page_location: window.location.href,
    page_title: pageTitle || document.title,
    ...extraParams,
  }));
};

export const trackEvent = (eventName, params = {}) => {
  if (!analyticsEnabled()) return;
  initAnalytics();
  window.gtag('event', sanitizeEventName(eventName), sanitizeEventParams(params));
};

export const setAnalyticsUserId = (userId) => {
  if (!analyticsEnabled()) return;
  initAnalytics();
  window.gtag('config', GA_MEASUREMENT_ID, sanitizeEventParams({
    user_id: userId || undefined,
  }));
};

export const trackWebVital = (metric) => {
  if (!metric) return;
  trackEvent('web_vital', {
    metric_name: metric.name,
    metric_id: metric.id,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    delta: Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta),
    rating: metric.rating,
  });
};

